import { useEffect, useState } from "react";
import { Bell, BellOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  currentSubscription,
  disablePush,
  enablePush,
  pushPermission,
  pushSupported,
} from "@/lib/push";

export default function PushSettings() {
  const [enabled, setEnabled] = useState(false);
  const [perm, setPerm] = useState(pushPermission());
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setPerm(pushPermission());
    const sub = await currentSubscription().catch(() => null);
    setEnabled(Boolean(sub));
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async (want) => {
    setBusy(true);
    try {
      if (want) {
        await enablePush();
        toast.success("Browser notifications enabled");
      } else {
        await disablePush();
        toast("Browser notifications disabled");
      }
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Could not update setting");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test push sent — check your OS notifications");
    } catch {
      toast.error("Test push failed");
    }
  };

  if (!pushSupported()) {
    return (
      <section className="soft-card p-4 sm:p-6" data-testid="push-settings">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-navy">
          <BellOff className="h-4 w-4" /> Browser notifications
        </h3>
        <p className="text-xs text-slate-500">
          This browser doesn&apos;t support push notifications. Try Chrome, Edge, or Firefox on desktop / Android.
        </p>
      </section>
    );
  }

  const denied = perm === "denied";

  return (
    <section className="soft-card p-4 sm:p-6" data-testid="push-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="mb-1 flex items-center gap-2 text-sm font-bold text-navy">
            <Bell className="h-4 w-4" /> Browser notifications
          </h3>
          <p className="text-xs text-slate-500">
            Get pinged the moment a lead is assigned to you, even when this tab isn&apos;t open.
          </p>
          {denied && (
            <p className="mt-2 text-xs text-amber-700">
              Permission is blocked. Click the lock icon in your address bar and allow notifications.
            </p>
          )}
        </div>
        <Switch
          data-testid="push-toggle"
          disabled={busy || denied}
          checked={enabled}
          onCheckedChange={toggle}
        />
      </div>
      {enabled && (
        <Button
          data-testid="push-test-btn"
          variant="outline"
          className="mt-4"
          onClick={sendTest}
        >
          <Sparkles className="h-4 w-4" /> Send test push
        </Button>
      )}
    </section>
  );
}
